import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Signal,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BlockLoadingComponent } from '@merchant-portal/app/components/loading/block-loading/block-loading.component';
import { OtpStoreFactory } from '@merchant-portal/app/core/otp/factory/otp-store.factory';
import { OtpComponent } from '@merchant-portal/app/core/otp/otp.component';
import { OtpStore } from '@merchant-portal/app/core/otp/store/otp.store';
import { CodeInputComponent, CodeInputModule } from 'angular-code-input';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';

import { Store } from '@ngrx/store';
import { ButtonComponent } from 'ui-component/src/public-api';
import { PinApiService } from '../../api/pin-api.service';
import { forgotPinFeature } from '../store/forgot-pin.reducer';
import { ForgotPinStoreFactory } from './factory/forgot-pin-store.factory';
import { ForgotPinDialogStoreService } from './services/change-pin-dialog-store.service';

@Component({
  selector: 'app-forgot-pin-dialog',
  imports: [
    CommonModule,
    CodeInputModule,
    CardModule,
    InputTextModule,
    ReactiveFormsModule,
    ButtonModule,
    BlockLoadingComponent,
    ButtonComponent,
    OtpComponent,
  ],
  templateUrl: './forgot-pin-dialog.component.html',
  styleUrls: ['./forgot-pin-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: ForgotPinStoreFactory,
      useClass: ForgotPinDialogStoreService,
    },
    {
      provide: OtpStoreFactory,
      useClass: OtpStore,
    },
    PinApiService,
  ],
})
export class ForgotPinDialogComponent {
  pinValue = '';

  private _userStep = signal<'input-otp' | 'create-pin' | 'confirm-pin' | 'done'>('input-otp');

  @ViewChild('codeInput') codeInput!: CodeInputComponent;

  store = inject(ForgotPinStoreFactory);
  ref = inject(DynamicDialogRef);
  otpStore = inject(OtpStoreFactory);
  private ngrxStore = inject(Store);

  // Step management
  userStep = this._userStep;

  // Get resendDelaySeconds from the NgRx store
  resendDelaySeconds = this.ngrxStore.selectSignal(forgotPinFeature.selectResendDelaySeconds);

  // Get twoFactorAuthMethod from the NgRx store
  twoFactorAuthMethod = this.ngrxStore.selectSignal(forgotPinFeature.selectTwoFactorAuthMethod);

  headerMap: Record<string, string> = {
    'input-otp': 'Reset PIN',
    'create-pin': 'Create New PIN',
    'confirm-pin': 'Confirm New PIN',
    done: 'PIN Successfully Changed',
  };

  subTitle: Signal<string> = computed(() => {
    const map: Record<string, string> = {
      'input-otp': 'Please enter the 6-digit verification code sent to your email.',
      'create-pin': 'Enter your new 6-digit number PIN',
      'confirm-pin': 'Re-enter your new 6-digit PIN',
      done: 'Your new PIN be used to securely authorize your transactions.',
    };
    const currentState = this.userStep();
    return map[currentState] ?? '';
  });

  _error$ = this.store.error$;
  error$ = computed(() => {
    const mappedError: Record<string, string> = {
      'invalid pin': 'PIN is incorrect',
    };
    return mappedError[this.store.error$() ?? '']
      ? mappedError[this.store.error$() ?? '']
      : this.store.error$();
  });
  isCurrentPin = true;
  isLoading = this.store.loading$;

  // OTP component inputs
  countDownStarted = true;

  constructor() {
    effect(() => {
      if (this.store.userStep() === 'confirm-pin') {
        this.codeInput.reset();
      }
    });
    effect(() => {
      const error = this.store.error$();
      if (this.store.userStep() === 'done' && !error) {
        this._userStep.set('done');
      }
    });

    // Listen to OTP store for verification success
    this.otpStore.vm$.subscribe(otpState => {
      if (otpState.isVeritifyOtpSuccess && this.userStep() === 'input-otp') {
        // OTP verified successfully, move to create PIN step
        this._userStep.set('create-pin');
      }
    });

    // Initialize OTP store properly when component loads
    this.initializeOtpSession();
  }

  private initializeOtpSession() {
    // Initialize OTP with proper session from backend response
    // The delay seconds should be retrieved from the OTP request response
    this.otpStore.setInitialSession('', this.resendDelaySeconds());
    this.otpStore.startCountdown();
  }

  onCodeChanged($event: string) {
    this.pinValue = $event;
  }

  async next() {
    const currentStep = this.userStep();
    if (currentStep === 'create-pin' || currentStep === 'confirm-pin') {
      // Use the store's next method for PIN validation and flow control
      await this.store.next(this.pinValue);

      // Update our local step based on the store's step
      const storeStep = this.store.userStep();
      this._userStep.set(storeStep);

      // Reset the input when moving to confirm step
      if (storeStep === 'confirm-pin') {
        this.codeInput.reset();
      }
    }
  }

  onOtpComplete(event: any) {
    // OTP verification completed successfully
    // Move to create PIN step
    this._userStep.set('create-pin');
  }

  cancel() {
    this.ref.close('cancel');
  }

  closeDialog() {
    this.ref.close('success');
  }
}
